import { Checkbox, DefaultButton, IconButton, MessageBar, MessageBarButton, MessageBarType, Pivot, PivotItem, PrimaryButton, Separator, Spinner, SpinnerSize, Stack, Text, TextField, Toggle } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/redux";
import { WebPanel } from "../../src/taskpane/components/_v2/_shared/WebPanel/WebPanel";
import { previewWebPanelStyles } from "../../src/taskpane/config/controlStyles";
import { LanguageContext } from "../../utilities/LocalizationModule";
import authModule from "../../utilities/AuthModule";
import {
    apiApplyTemplatePlaceholderProposals,
    apiPreviewTemplate,
    apiRenderProposalSlide,
    apiScanTemplateCustomFields,
    apiSuggestTemplatePlaceholders,
} from "../../src/taskpane/services/ApiService";
import {
    PreviewTemplateResponse,
    RenderProposalSlideResponse,
    SuggestTemplatePlaceholdersResponse,
    TemplateAiProposal,
    TemplateScanResult,
} from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { useLazyGetFileByIDQuery, useLazyRestoreVersionByLabelQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { buildSlideRenderKey, createSlideRenderCache, ISlideRenderCache } from "./slideRenderCache";

export interface ICMPlaceholderSuggestPanelProps {
    isOpen: boolean;
    closePanel: () => void;
    templateUniqueId: string;
    templateFileName: string;
    siteUrl: string;
    listId: string;
    /** Fired after a successful apply so the host can refresh caches / show the scan bar. */
    onApplied?: (scanErrors: string[]) => void;
}

/** Two transports feed this function. Suggest/apply are axios: a rejection shaped
 *  `{ response: { data } }`, `data` carrying anything from a plain-string 409 body to a
 *  ProblemDetails object to an empty 400 body. Undo's `restoreVersionByLabel` is RTK Query
 *  over `fetchBaseQuery`: a rejected `.unwrap()` is a `FetchBaseQueryError` shaped
 *  `{ status, data }`, `data` being the server's own body — string or object. Only a
 *  non-empty STRING may ever reach the MessageBar — rendering an object crashes the React
 *  subtree; an empty string renders nothing — so every branch below stays string-checked and
 *  anything unrecognised falls through to the caller's `fallback`. What this function will
 *  NOT do is restate a transport-level message; see the closing comment. */
const errorText = (error: any, fallback: string): string => {
    const responseData = error?.response?.data;
    if (typeof responseData === "string" && responseData.trim().length > 0) return responseData;
    if (responseData && typeof responseData === "object") {
        const detail = (responseData as { detail?: unknown; title?: unknown }).detail ?? (responseData as { title?: unknown }).title;
        if (typeof detail === "string" && detail.trim().length > 0) return detail;
    }

    // FetchBaseQueryError.data: the server's response body for a rejected RTK Query call.
    const queryData = (error as { data?: unknown })?.data;
    if (typeof queryData === "string" && queryData.trim().length > 0) return queryData;
    if (queryData && typeof queryData === "object") {
        const message = (queryData as { error?: unknown; message?: unknown }).error ?? (queryData as { message?: unknown }).message;
        if (typeof message === "string" && message.trim().length > 0) return message;
    }

    // DELIBERATELY NO `error.message` BRANCH. One lived here briefly, for RTK Query's
    // SerializedError (the call never reached the server — offline, or a throw before fetch).
    // It could not tell that apart from ANY other thrown Error, and the realistic one is an
    // MSAL failure inside baseRequest(), which reaches the suggest and apply catches too: a
    // Dutch or German user was shown `no_account_error: …` verbatim. The branches above are
    // the ones that carry a SERVER's reason, which is the whole reason to prefer a raw string
    // over the localized fallback; a transport-level message is not, and for undo the fallback
    // ("restore version 9.0 from version history") is more actionable than "Failed to fetch".
    return fallback;
};

/**
 * The preview is a picture of the DOCUMENT, so it keeps a white "paper" surface in BOTH app
 * themes. Two reasons, and the literal colours are deliberate rather than --db-* tokens:
 *  - the exported HTML carries the template's own colours (an allowlisted `color:#000000`
 *    on a heading survives sanitisation), and those were authored for white paper;
 *  - the PowerPoint arm of this same panel shows a rasterised slide, which is white paper
 *    whatever the app theme does — a dark HTML arm next to it reads as two products.
 * A theme-reactive token here would flip the paper in dark mode and bring both back.
 */
const previewPaperStyle: React.CSSProperties = {
    background: "#FFFFFF",
    color: "#201F1E",
    // Keeps UA-painted bits (scrollbars, form controls in an exported table) light so they
    // don't sit as dark rectangles on the paper.
    colorScheme: "light",
};

/** Anchor-chip bg/fg pairs, contrast-checked against the paper above. The fg half is the
 *  fix for the unreadable chips: only the background was ever set, so the text inherited
 *  the app's --db-fg — near-white in dark mode, on a near-white chip. */
const CHIP_ACCEPTED = { background: "#DFF6DD", color: "#0B6A0B" };
const CHIP_ORIGINAL = { background: "#EDEBE9", color: "#323130" };
const CHIP_FOCUS_OUTLINE = "2px solid #0078D4";

/** The anchor as it appears on a card. A chip, not bracket glyphs: the ▸…◂ this replaces was
 *  read as garbled characters, and a background marks the span more clearly than punctuation
 *  the anchor text could itself contain. These ARE theme tokens (unlike the preview paper) —
 *  the card list is app surface.
 *
 *  When the proposal carries a valueText, only THAT part becomes the token, so only that part
 *  is emphasised and the surviving caption is shown dimmed inside the same chip. Without the
 *  distinction the card would claim "Name: ___________" is replaced when "Name: " stays. */
const renderAnchor = (proposal: TemplateAiProposal) => {
    const anchor = proposal.anchorText ?? "";
    const at = proposal.valueText ? anchor.indexOf(proposal.valueText) : -1;
    const [before, value, after] = at < 0
        ? ["", anchor, ""]
        : [anchor.slice(0, at), proposal.valueText, anchor.slice(at + proposal.valueText.length)];

    return (
        <span
            style={{
                background: "var(--db-chip-bg, #faf9f8)",
                border: "1px solid var(--db-chip-border, #c5c5c5)",
                borderRadius: 2,
                color: "var(--db-fg-muted, #605e5c)",
                padding: "0 4px",
            }}
        >
            {before}
            <span style={{ color: "var(--db-fg, #323130)", fontWeight: 600 }}>{value}</span>
            {after}
        </span>
    );
};

/**
 * Freezes each anchor span at the width of its WIDER state, so toggling a proposal swaps the
 * text without reflowing the document around it.
 *
 * A token (`<<[fortes:Name]>>`) is usually longer than the text it replaces ("Acme B.V."), so
 * ticking a checkbox grew the span, pushed the rest of the paragraph along, and moved the page
 * under the user - sometimes shifting the very anchor they were looking at out from under the
 * pointer. Reserving max(original, token) up front means neither state can be the one that
 * grows.
 *
 * `display: inline-block` is a precondition, not a flourish: `min-width` has no effect on an
 * inline box. The cost is that a reserved anchor can no longer break across lines - acceptable
 * on a surface already captioned "layout approximate", and anchors are short by construction.
 *
 * Measured in TWO batched passes rather than per span. Alternating a write and a read per span
 * would force a synchronous layout each time - 2N reflows for N anchors; writing all, reading
 * all, then writing all again costs two. Runs once per span, marked by data-reserved, because
 * this is called on every toggle.
 */
const reserveSpanWidths = (host: HTMLElement) => {
    const spans = Array.from(host.querySelectorAll<HTMLElement>("[data-proposal]:not([data-reserved])"));
    if (spans.length === 0) return;

    const originals = spans.map(span => span.getAttribute("data-original") ?? "");
    const tokens = spans.map(span => span.getAttribute("data-token") ?? "");

    spans.forEach(span => { span.style.display = "inline-block"; });
    spans.forEach((span, index) => { span.textContent = originals[index]; });
    const originalWidths = spans.map(span => span.offsetWidth);
    spans.forEach((span, index) => { span.textContent = tokens[index]; });
    const tokenWidths = spans.map(span => span.offsetWidth);

    spans.forEach((span, index) => {
        const reserved = Math.max(originalWidths[index], tokenWidths[index]);
        // A zero measurement means the span was not laid out - an ancestor still display:none,
        // or measured before the panel had a box. Stamping data-reserved on that would bake a
        // 0px reservation in permanently and silently disable this for that span, so leave it
        // unmarked and let the next syncSpans (there is one per toggle) try again.
        if (reserved <= 0) return;
        span.style.minWidth = `${reserved}px`;
        span.setAttribute("data-reserved", "1");
    });
};

/** What the SERVER did, captured at apply time, plus the review the apply consumed. Not a
 *  phase: the panel stays in the review layout and renders this as report bars. */
interface IApplyReport {
    appliedCount: number;
    submittedCount: number;
    skipped: { label?: string; anchorText?: string; reason?: string }[];
    /** The label the template carried BEFORE this apply: what undo restores. */
    previousVersion: string;
    /** The review as it stood when apply consumed it. The rewrite invalidates it (anchors
     *  and offsets describe bytes the server replaced) — but an undo restores those exact
     *  bytes, so undo hands this back verbatim rather than spending a model call and a
     *  daily-cap slot to rediscover it. */
    snapshot: {
        result: SuggestTemplatePlaceholdersResponse;
        checked: Set<number>;
        replaceAll: Set<number>;
    };
}

/**
 * Review-before-apply panel (design §7): live preview left, proposal cards right, two-way
 * sync, click-to-toggle in the preview. The preview DOM is NEVER read for apply — apply
 * re-submits the full accepted proposals and the server re-validates (stateless contract).
 */
export const CMPlaceholderSuggestPanel = (props: ICMPlaceholderSuggestPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const userAccount = useSelector((state: RootState) => state.user?.account);

    // TWO flags, not one "isBusy", and the split is the point: both runs used to share a single
    // flag, so opening the panel — which fetches the DOCUMENT and asks the model nothing — spun
    // the Suggest button as though a suggestion were being generated. A spinner has to name the
    // thing it is waiting on, and on open that is the preview, not the button.
    const [isSuggesting, setIsSuggesting] = React.useState(false);
    // Seeded from isOpen rather than starting false, because `useEffect` runs AFTER the browser
    // paints: with a false seed the panel's FIRST frame painted an enabled Suggest button over an
    // empty preview column, and only the frame after that showed the load. That frame is
    // clickable, and Fluent's Panel lengthens it - it renders null on mount and only mounts these
    // children in componentDidMount, so the first paint lands inside the entrance animation.
    // An open panel is loading its preview by definition; the effect below starts the request,
    // it does not decide the state.
    const [isPreviewing, setIsPreviewing] = React.useState(props.isOpen);
    const [isApplying, setIsApplying] = React.useState(false);
    const [instruction, setInstruction] = React.useState("");
    const [result, setResult] = React.useState<SuggestTemplatePlaceholdersResponse | undefined>();
    const [checked, setChecked] = React.useState<Set<number>>(new Set());
    const [replaceAll, setReplaceAll] = React.useState<Set<number>>(new Set());
    const [message, setMessage] = React.useState<string | undefined>();
    const [focusedProposal, setFocusedProposal] = React.useState<number | undefined>();
    const [applyReport, setApplyReport] = React.useState<IApplyReport | undefined>();
    /** What the host must be told at close, or undefined if the template was never written.
     *  Deliberately NOT derived from `applyReport`: a revert clears the report, and the user
     *  can dismiss its bar outright, but the file has still been written and its
     *  custom-fields column re-scanned, so the grid row still has to be refreshed. Reading
     *  the report off the apply state is exactly how an undo used to leave the host
     *  uninformed. */
    const [hostScanErrors, setHostScanErrors] = React.useState<string[] | undefined>();
    /** Held apart from `result` on purpose. The note describes the TENANT's configuration -
     *  which CRM family supplies client fields, or which prerequisite is missing - not the run.
     *  Read off `result` it vanished on every Suggest press (runSuggest clears `result` first)
     *  and came back seconds later, so a fixed fact about the tenant blinked once per run.
     *  Assigned from every SUCCESSFUL response, including one that carries no note, so a
     *  configuration that has since been fixed clears it instead of leaving a stale warning;
     *  a failed suggest leaves the last known note standing. */
    const [vocabularyNote, setVocabularyNote] = React.useState<string | undefined>();
    /** False until a suggest run has actually come back. The panel now opens on a preview with
     *  no proposals, which is indistinguishable from "the model found nothing" if you only look
     *  at the proposal count - and "No suggestions found for this template" greeting someone who
     *  has not yet asked for any is a lie about work that never ran. */
    const [hasSuggested, setHasSuggested] = React.useState(false);

    const previewRef = React.useRef<HTMLDivElement | null>(null);
    // React 16: setState after unmount logs warnings; every await guards on this.
    const aliveRef = React.useRef(true);
    React.useEffect(() => () => { aliveRef.current = false; }, []);
    // Toggling while apply is in flight would silently diverge from what the server is
    // writing (apply snapshots `checked` at click time) — a ref so the guards don't have
    // to re-create every callback.
    const isApplyingRef = React.useRef(false);

    const baseRequest = React.useCallback(async () => ({
        accessToken: await authModule.getSharePointAccessToken(),
        tenantGuid: userAccount?.tenantId,
        siteUrl: props.siteUrl,
        listId: props.listId,
        templateUniqueId: props.templateUniqueId,
    }), [userAccount, props.siteUrl, props.listId, props.templateUniqueId]);

    /**
     * The template as it stands, with nothing proposed on it. Runs on open in place of the
     * suggestion that used to fire there.
     *
     * The old auto-suggest made the instruction field unusable: the effect fired before anyone
     * could type, so the first run was always un-steered and steering it meant running a second
     * time - two model calls and two slices of the daily cap to use a box the panel puts in
     * front of you. Opening on the document instead lets the author read it, write an
     * instruction, and spend the first suggestion on the run they actually wanted.
     *
     * Costs no AI quota (PreviewTemplate takes no rate-limiter slot), so an accidental open, or
     * one that only ever wanted a look at the file, now bills nothing.
     */
    const runPreview = React.useCallback(async () => {
        setIsPreviewing(true);
        setMessage(undefined);
        try {
            const response: PreviewTemplateResponse = await apiPreviewTemplate(await baseRequest());
            if (!aliveRef.current) return;
            if (!response) {
                setMessage(languageStrings.ErrorMessageGeneric);
                return;
            }
            // Shaped as a suggest response with nothing found, so the whole render path
            // (preview arms, span sync, slide filmstrip) works unchanged. `hasSuggested` is
            // what keeps it from reading as "the model found nothing".
            setResult({ proposals: [], rejected: [], preview: response.preview, fileVersion: response.fileVersion });
            setMessage(response.message);
        } catch (error: any) {
            if (aliveRef.current) setMessage(errorText(error, languageStrings.ErrorMessageGeneric));
        } finally {
            if (aliveRef.current) setIsPreviewing(false);
        }
    }, [baseRequest, languageStrings]);

    const runSuggest = React.useCallback(async () => {
        setIsSuggesting(true);
        setMessage(undefined);
        // The PROPOSALS are dropped, the preview is not: blanking `result` outright pulled the
        // document off the screen for the length of every run, and with the panel now opening
        // on that document there is always one worth keeping.
        setResult(previous => previous && { ...previous, proposals: [], rejected: [] });
        setChecked(new Set());
        setReplaceAll(new Set());
        try {
            const response: SuggestTemplatePlaceholdersResponse = await apiSuggestTemplatePlaceholders({
                ...(await baseRequest()),
                instruction: instruction?.trim() || undefined,
            });
            if (!aliveRef.current) return;
            if (!response) {
                setMessage(languageStrings.ErrorMessageGeneric);
                return;
            }
            // MERGED, never assigned wholesale. Every refusal in SuggestAsync returns before
            // BuildPreview - daily cap reached, model error, download failure, unsupported
            // extension, feature disabled - so it carries a message and NOTHING else. Assigning
            // it destroyed the preview the panel had opened on and the version that came with
            // it: hitting the tenant's daily cap made the document disappear behind a bare
            // error, and blanked the fileVersion that a later apply has to submit.
            // A completed run REPLACES everything, including a fileVersion the server read as
            // empty - that is a fresher read of the same property, and falling back to the
            // preview's would offer an Undo restoring a label the file may not have. Only a
            // refusal, which read nothing, keeps what the panel already had.
            setResult(previous => response.preview
                ? response
                : { ...response, preview: previous?.preview, fileVersion: response.fileVersion || previous?.fileVersion });
            // A preview is only built once a run has fully completed, which makes it the one
            // reliable "this response describes the template" signal. A refusal never assembled
            // the vocabulary, so it says nothing about the tenant's datasources and must not
            // clear a note that still holds - the same reasoning as the catch below.
            if (response.preview) setVocabularyNote(response.vocabularyNote);
            setMessage(response.message);
            setChecked(new Set((response.proposals ?? []).filter(p => p.defaultChecked).map(p => p.id)));
            setReplaceAll(new Set());
            // Only after a run that actually reached the model: a thrown request says nothing
            // about whether the template has anything to suggest.
            setHasSuggested(true);
        } catch (error: any) {
            // The api wrapper rethrows non-auth errors; without this catch a 400/500 kills
            // the panel silently (unhandled rejection, spinner stops, nothing shown).
            if (aliveRef.current) setMessage(errorText(error, languageStrings.ErrorMessageGeneric));
        } finally {
            if (aliveRef.current) setIsSuggesting(false);
        }
    }, [baseRequest, instruction, languageStrings]);

    // Opens on the DOCUMENT, not on a suggestion. See runPreview for why the auto-suggest that
    // used to live here was worth removing.
    React.useEffect(() => {
        if (props.isOpen) runPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.templateUniqueId]);

    const toggleProposal = React.useCallback((id: number) => {
        // The in-flight apply snapshotted `checked`; an in-flight undo is about to overwrite
        // it from the snapshot - either way a toggle now would silently diverge.
        if (isApplyingRef.current || isUndoingRef.current) return;
        setChecked(previous => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    // ---------------------------------------------------------------- live span sync
    // The server renders the initial state; every toggle after that swaps span text
    // client-side from data-original/data-token. Zero round-trips for Word/Excel.
    const syncSpans = React.useCallback((host: HTMLElement | null) => {
        if (!host) return;
        reserveSpanWidths(host);
        host.querySelectorAll<HTMLElement>("[data-proposal]").forEach(span => {
            const id = Number(span.getAttribute("data-proposal"));
            const occurrence = Number(span.getAttribute("data-occurrence") ?? "1");
            // data-occurrence is numbered PER PROPOSAL, so this needs no notion of pinning: a
            // pinned proposal is wrapped exactly once, as its own occurrence 1, and therefore
            // lights up whenever it is checked. (No applied branch any more: after an apply
            // the panel fetches and shows the server's own document, which carries no spans.)
            const isOn = checked.has(id) && (occurrence === 1 || replaceAll.has(id));
            span.textContent = isOn
                ? span.getAttribute("data-token") ?? ""
                : span.getAttribute("data-original") ?? "";
            const chip = isOn ? CHIP_ACCEPTED : CHIP_ORIGINAL;
            span.style.backgroundColor = chip.background;
            // Set with the background, never separately: the export may put its own `color`
            // on the run this chip sits in, and half a pair is how a chip goes unreadable.
            span.style.color = chip.color;
            span.style.outline = focusedProposal === id ? CHIP_FOCUS_OUTLINE : "none";
            span.style.cursor = "pointer";
        });
    }, [checked, replaceAll, focusedProposal]);

    React.useEffect(() => {
        syncSpans(previewRef.current);
    }, [syncSpans, result]);

    // Pivot unmounts inactive tabs, so a freshly shown sheet mounts with the server's
    // INITIAL html; syncing in the ref callback (not just the effect) brings it to the
    // current checked/replaceAll state the moment it appears.
    const attachPreviewHost = React.useCallback((node: HTMLDivElement | null) => {
        previewRef.current = node;
        if (node) syncSpans(node);
    }, [syncSpans]);

    const onPreviewClick = React.useCallback((event: React.MouseEvent) => {
        const span = (event.target as HTMLElement).closest?.("[data-proposal]") as HTMLElement | null;
        if (!span) return;
        const id = Number(span.getAttribute("data-proposal"));
        toggleProposal(id);
        setFocusedProposal(id);
    }, [toggleProposal]);

    const scrollPreviewTo = React.useCallback((id: number) => {
        setFocusedProposal(id);
        // Word/Excel: the span lives in the preview DOM. PowerPoint has no span to scroll to
        // (the preview is a rendered image), so SlidePreview navigates on focusedProposal
        // instead — without that, clicking a card on the PPT path did nothing at all.
        previewRef.current
            ?.querySelector(`[data-proposal="${id}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, []);

    // ---------------------------------------------------------------- apply

    const apply = React.useCallback(async () => {
        if (!result || isApplyingRef.current) return;
        const accepted: TemplateAiProposal[] = (result.proposals ?? [])
            .filter(p => checked.has(p.id))
            .map(p => ({ ...p, replaceAll: replaceAll.has(p.id) }));
        if (accepted.length === 0) return;

        isApplyingRef.current = true;
        setIsApplying(true);
        setMessage(undefined);
        try {
            const response = await apiApplyTemplatePlaceholderProposals({
                ...(await baseRequest()),
                fileVersion: result.fileVersion ?? "",
                acceptedProposals: accepted,
            });
            if (!aliveRef.current) return;

            const appliedIds: number[] = response?.appliedProposalIds ?? [];
            const skipped: { label?: string; anchorText?: string; reason?: string }[] = response?.skipped ?? [];
            const scanErrors: string[] = (response?.scanResult?.errors ?? []).map((e: string) => `${props.templateFileName}: ${e}`);

            if (appliedIds.length === 0) {
                // Nothing was written (the server also skipped the file rewrite): stay in the
                // review phase so the situation is visible and the user can re-run suggestions.
                setMessage(`${languageStrings.SuggestPlaceholdersNoneApplied} ${skipped.map(s => s.reason).filter(Boolean).join("; ")}`);
                return;
            }

            // The report renders NOW; the fresh preview arrives when it arrives. Confirmation
            // must never wait on the network.
            setHostScanErrors(scanErrors);
            setWriteScanErrors(scanErrors);
            setApplyReport({
                appliedCount: appliedIds.length,
                submittedCount: accepted.length,
                skipped,
                previousVersion: result.fileVersion ?? "",
                snapshot: { result, checked, replaceAll },
            });
            // The rewrite invalidates everything the review knew: anchors, offsets and the
            // preview all describe bytes the server just replaced. If the fetch below fails
            // they must not resurface behind a stale label - that is the 409 the old design
            // hid the whole review behind a result screen to avoid.
            setResult({ proposals: [], rejected: [], preview: undefined, fileVersion: result.fileVersion });
            setChecked(new Set());
            setReplaceAll(new Set());
            setHasSuggested(false);
            setRevertNote(undefined);
            setFocusedProposal(undefined);
            // Deliberately not awaited: the write is done, so isApplying ends with it and
            // isPreviewing owns the wait - the report must not sit behind a spinner.
            runPreview();
        } catch (error: any) {
            // 409 carries the version-conflict / locked-file guidance verbatim (a string
            // body); anything else degrades to the localized generic.
            if (aliveRef.current) setMessage(errorText(error, languageStrings.ErrorMessageGeneric));
        } finally {
            isApplyingRef.current = false;
            if (aliveRef.current) setIsApplying(false);
        }
    }, [result, checked, replaceAll, baseRequest, props, languageStrings, runPreview]);

    // ---------------------------------------------------------------- undo

    const [restoreVersionByLabel] = useLazyRestoreVersionByLabelQuery();
    const [getFileByID] = useLazyGetFileByIDQuery();
    const [isUndoing, setIsUndoing] = React.useState(false);
    /** Shown in the REVIEW phase after a revert, because that is where a revert now lands. */
    const [revertNote, setRevertNote] = React.useState<string | undefined>();
    /** The last write's scan outcome — the apply's scan errors, or the post-revert rescan's
     *  — shown as a warning bar in the review. Whichever write happened last owns this bar,
     *  exactly as it owns the file. Not folded into `message` because every run clears that
     *  slot, while a scan fact outlives all of them; not derived from `hostScanErrors`
     *  because THAT must keep carrying the report after this bar is dismissed — the host's
     *  close-time bar is the durable record. Assigned on every write, so a clean outcome
     *  clears the previous write's warnings. */
    const [writeScanErrors, setWriteScanErrors] = React.useState<string[]>([]);
    // Same idiom as isApplyingRef above: the disabling re-render from setIsUndoing has not
    // flushed yet when a second same-tick invocation (e.g. a double-click) arrives, so the
    // guard needs a synchronous flag, not just the state.
    const isUndoingRef = React.useRef(false);

    /**
     * Restores the version the template carried BEFORE the apply, then hands the user back the
     * review they were in the middle of - same proposals, same ticks, live preview.
     *
     * That is safe for a reason worth writing down: the restore puts back the exact bytes the
     * suggest run analysed, so every anchor, offset and context in the snapshot still describes
     * the file. The ONLY thing the restore invalidates is the version LABEL - SharePoint's
     * restore is itself a new version - and apply compares that label strictly
     * (TemplateAiSuggestOrchestrator: "the template changed since suggestions were generated").
     * So the label is re-read here and patched into the snapshot as it is handed back;
     * everything else is left alone.
     * Re-running suggest instead would spend a model call and a slice of the daily cap to
     * rediscover proposals we already have, and would throw away the user's ticks.
     *
     * Before this, the panel stayed on the result screen with nothing but Close, so undoing meant
     * closing and re-opening the panel to change anything.
     */
    const undo = React.useCallback(async () => {
        if (!applyReport || isUndoingRef.current) return;
        isUndoingRef.current = true;
        setIsUndoing(true);
        setMessage(undefined);
        try {
            await restoreVersionByLabel({
                siteUrl: props.siteUrl,
                uniqueId: props.templateUniqueId,
                accessToken: await authModule.getSharePointAccessToken(),
                versionLabel: applyReport.previousVersion,
            }).unwrap();

            // The apply's rescan wrote a custom-fields envelope for content that no longer
            // exists. A scan failure here must NOT turn a successful revert into a reported
            // failure - the file is already back.
            let scanErrors: string[] = [];
            try {
                const scan = await apiScanTemplateCustomFields({
                    accessToken: await authModule.getSharePointAccessToken(),
                    tenantGuid: userAccount?.tenantId,
                    accountId: userAccount?.accountId,
                    siteUrl: props.siteUrl,
                    listId: props.listId,
                    templateUniqueIds: [props.templateUniqueId],
                });
                scanErrors = ((scan?.value ?? []) as TemplateScanResult[])
                    .flatMap(entry => (entry.errors ?? []).map(error => `${entry.fileName}: ${error}`));
            } catch {
                // The restore above already succeeded, so this failure must never be reported
                // as a FAILED UNDO - the file is back. But nothing re-triggers the scan
                // afterwards, so the custom-fields column may keep describing content the file
                // no longer has - and a silent [] here reported precisely the clean revert
                // that did not happen. The sentinel line keeps the hazard visible in-panel and
                // on the host's bar after close, and names the remedy (a manual re-scan).
                scanErrors = [`${props.templateFileName}: ${languageStrings.SuggestPlaceholdersRevertScanFailed}`];
            }

            // Re-read the label the restore just minted. Best effort: a failure here does not
            // undo the undo, it only means apply would be refused on a stale label - so the
            // user is sent back to review anyway, told to re-run Suggest first, rather than
            // stranded on a result screen.
            let refreshedVersion: string | undefined;
            try {
                const file = await getFileByID({
                    siteUrl: props.siteUrl,
                    uniqueId: props.templateUniqueId,
                    accessToken: await authModule.getSharePointAccessToken(),
                }).unwrap();
                const label = (file as { UIVersionLabel?: unknown })?.UIVersionLabel;
                if (typeof label === "string" && label.length > 0) refreshedVersion = label;
            } catch {
                // Falls through to the stale-version note below.
            }

            if (!aliveRef.current) return;
            // Apply invalidated the review; the restore un-invalidates it. The restored
            // bytes are the ones suggest analysed, so the snapshot is valid again the
            // instant the restore lands - only the version label moved, so only the label
            // is patched.
            const { snapshot } = applyReport;
            setResult({ ...snapshot.result, fileVersion: refreshedVersion ?? snapshot.result.fileVersion });
            setChecked(snapshot.checked);
            setReplaceAll(snapshot.replaceAll);
            setHasSuggested(true);
            setHostScanErrors(scanErrors);
            setWriteScanErrors(scanErrors);
            setApplyReport(undefined);
            setRevertNote(refreshedVersion
                ? languageStrings.SuggestPlaceholdersReverted
                : languageStrings.SuggestPlaceholdersRevertedStale);
        } catch (error: any) {
            // Keeps the report on failure, Undo still offered: the apply genuinely happened
            // and is still the state of the file, so there is still something to undo.
            // The version label survives in THIS message only - the user may now have to restore
            // by hand, and the label is the one thing that says which entry to pick.
            if (aliveRef.current) {
                setMessage(errorText(
                    error,
                    languageStrings.SuggestPlaceholdersUndoFailed.replace("{0}", applyReport.previousVersion)));
            }
        } finally {
            isUndoingRef.current = false;
            if (aliveRef.current) setIsUndoing(false);
        }
    }, [applyReport, props, userAccount, languageStrings, restoreVersionByLabel, getFileByID]);

    // The host raises a PERSISTENT bar and refreshes the row. Doing either while this panel
    // still covers the grid would put the report underneath it, and close is also the only
    // moment the outcome is final - an undo rewrites it.
    //
    // While EITHER write is in flight this is a deliberate no-op, not just disabled-looking.
    // For undo, a close would report the scan errors as they stood BEFORE the restore, or
    // (once unmounted) nothing at all - undo's own aliveRef guard swallows both the success and
    // the failure of a restore nobody then hears about. For apply the loss is larger and the
    // temptation greater: it is the LONG operation (checkout, parse, rewrite, check-in, rescan),
    // and closing mid-write let the template be rewritten and a new major version checked in
    // while the panel vanished, the host was never told, and the grid row was never refreshed -
    // the exact silent close this panel was rebuilt to eliminate. Briefly refusing to dismiss is
    // the lesser evil next to silently losing the outcome of a write. Both flags are the
    // synchronous refs, not the render state: the click can arrive in the same tick as the one
    // that started the write.
    const closeWithOutcome = React.useCallback(() => {
        if (isUndoingRef.current || isApplyingRef.current) return;
        if (hostScanErrors) props.onApplied?.(hostScanErrors);
        props.closePanel();
    }, [hostScanErrors, props]);

    // ---------------------------------------------------------------- render

    const proposals = result?.proposals ?? [];
    const rejected = result?.rejected ?? [];

    const renderCard = (proposal: TemplateAiProposal) => (
        <Stack
            key={proposal.id}
            tokens={{ childrenGap: 4 }}
            styles={{
                root: {
                    padding: 10,
                    cursor: "pointer",
                    // Drawn as an inset pseudo-element rather than a borderBottom on the card.
                    // A border runs the full 400px of the column and so collides with the vertical
                    // divider on the left; the Separator in the block above is inset by its
                    // parent's 10px padding, and the two rules reading differently is what made
                    // these look wrong. left/right 10 lines this up with that Separator exactly.
                    // The card itself stays full-bleed so the click target is unchanged - which is
                    // the reason for the pseudo-element instead of just adding side margins.
                    position: "relative",
                    selectors: {
                        "::after": {
                            content: '""',
                            position: "absolute",
                            left: 10,
                            right: 10,
                            bottom: 0,
                            borderBottom: "1px solid var(--db-border, #EDEBE9)",
                        },
                        // The rule separates one card from the NEXT one, so the last card in the
                        // column must not draw it - directly above the action bar it reads as the
                        // bar's own edge rather than a separator.
                        // `:last-child` rather than an index check on purpose: when the "Not
                        // applicable" block follows the cards, the final card is no longer last
                        // and KEEPS its rule, which is correct - there it is dividing the two
                        // lists, and the rejected block (which draws no rule of its own) is what
                        // meets the bar.
                        ":last-child::after": { display: "none" },
                    },
                },
            }}
            onClick={() => scrollPreviewTo(proposal.id)}
        >
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Checkbox
                    checked={checked.has(proposal.id)}
                    disabled={isApplying || isUndoing}
                    onChange={(ev) => { ev?.stopPropagation(); toggleProposal(proposal.id); }}
                />
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>{proposal.displayLabel}</Text>
                {proposal.inHeaderOrFooter && (
                    <Text variant="tiny" styles={{ root: { opacity: 0.7 } }}>({languageStrings.SuggestPlaceholdersHeaderBadge})</Text>
                )}
            </Stack>
            {proposal.location && <Text variant="small" styles={{ root: { opacity: 0.75 } }}>{proposal.location}</Text>}
            <Text variant="small">
                {proposal.contextBefore}
                {renderAnchor(proposal)}
                {proposal.contextAfter}
            </Text>
            <Text variant="tiny" styles={{ root: { fontFamily: "monospace", opacity: 0.8 } }}>{proposal.token}</Text>
            {/* A pinned proposal gets a read-out, not the replace-all toggle: the server drops
                ReplaceAll for pinned proposals (ReplacePlan.For), so offering the toggle would
                promise a widening that silently never happens. */}
            {proposal.occurrenceIndex != null && (
                <Text variant="tiny" styles={{ root: { opacity: 0.75 } }}>
                    {languageStrings.SuggestPlaceholdersPinnedOccurrence
                        .replace("{0}", String(proposal.occurrenceIndex))
                        .replace("{1}", String(proposal.occurrences))}
                </Text>
            )}
            {proposal.occurrenceIndex == null && proposal.occurrences > 1 && (
                <Toggle
                    label={`${languageStrings.SuggestPlaceholdersReplaceAll} (${proposal.occurrences}×)`}
                    inlineLabel
                    checked={replaceAll.has(proposal.id)}
                    disabled={isApplying || isUndoing}
                    onChange={(ev, value) => {
                        ev?.stopPropagation();
                        if (isApplyingRef.current || isUndoingRef.current) return;
                        setReplaceAll(previous => {
                            const next = new Set(previous);
                            if (value) next.add(proposal.id); else next.delete(proposal.id);
                            return next;
                        });
                    }}
                />
            )}
        </Stack>
    );

    const renderPreview = () => {
        // The open-time wait belongs HERE, in the column that is actually empty, not on the
        // Suggest button. Deliberately NOT shown for a suggest run: that run keeps the document
        // on screen (see runSuggest), so covering it with a spinner would undo the very thing
        // that stopped the panel blanking on every attempt.
        //
        // `grow` + minHeight 0 to claim the column's leftover height under the "layout
        // approximate" caption, then centre on both axes within it - on the panel background
        // rather than a white page, because there is no document yet to imply one, and the
        // paper surface is pinned light and would sit as a white slab in dark mode.
        if (isPreviewing) {
            return (
                <Stack grow horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 0 } }}>
                    <Spinner size={SpinnerSize.large} label={languageStrings.Loading} />
                </Stack>
            );
        }

        const preview = result?.preview;
        if (!preview || preview.kind === "none") return null;

        if (preview.kind === "html") {
            return (
                <div
                    ref={attachPreviewHost}
                    onClick={onPreviewClick}
                    // flexGrow rather than `height: 100%`: the column also carries the
                    // "layout approximate" caption, so a full-height child overflowed it by
                    // the caption's height and the bottom of the document was clipped.
                    style={{ ...previewPaperStyle, overflow: "auto", flexGrow: 1, minHeight: 0, padding: 16 }}
                    // Server-side allowlist-sanitized fragment (TemplateAiPreviewHtml):
                    // inert markup by construction, no scripts/external refs can survive.
                    dangerouslySetInnerHTML={{ __html: preview.html ?? "" }}
                />
            );
        }
        if (preview.kind === "sheets") {
            return (
                <Pivot styles={{ root: { height: "100%" } }}>
                    {(preview.sheets ?? []).map(sheet => (
                        <PivotItem headerText={sheet.name} key={sheet.name}>
                            {/* attachPreviewHost (not a plain ref): Pivot unmounts inactive
                                tabs, and a remounted sheet must sync to the CURRENT toggle
                                state, not the server-initial html. */}
                            {/* The one arm still sized off the viewport. Fluent's Pivot puts the
                                selected sheet in an itemContainer inside an unstyleable wrapper
                                div (its `className` prop is folded into the TAB LIST, so a flex
                                class there would stack the tabs vertically), so there is no flex
                                chain to hang this off. Nothing is subtracted for the action bar:
                                it lives inside the CARD column, not under this one. */}
                            <div
                                ref={attachPreviewHost}
                                onClick={onPreviewClick}
                                style={{ ...previewPaperStyle, overflow: "auto", height: "calc(100vh - 220px)", padding: 16 }}
                                dangerouslySetInnerHTML={{ __html: sheet.html ?? "" }}
                            />
                        </PivotItem>
                    ))}
                </Pivot>
            );
        }
        return (
            <SlidePreview
                slideCount={preview.slideCount ?? 0}
                fileVersion={result?.fileVersion ?? ""}
                proposals={proposals}
                checked={checked}
                replaceAll={replaceAll}
                baseRequest={baseRequest}
                onToggle={toggleProposal}
                focusedProposal={focusedProposal}
            />
        );
    };

    /**
     * The panel's commit and dismiss actions: an end-aligned Stack carrying the same content, in
     * the same order (primary last), as the Fluent `onRenderFooterContent` footer the other 17
     * panels in this app use — but STICKY INSIDE THE CARD COLUMN rather than in Fluent's footer
     * slot. It has to leave that slot to buy the preview its full height:
     *
     * Fluent nests the footer as an in-flow sibling of `.ms-Panel-content`, and `position:
     * sticky` does NOT take a box out of flow — a sticky element still reserves its full height.
     * So for as long as the bar lives there, `content` is `scrollableContent − commands − footer`
     * and the preview is permanently ~65px short, whatever the flex chain does. The only way to
     * stop paying that is for the bar to stop being an in-flow sibling of the preview. Here it is
     * a sibling of the CARDS instead, so only that column pays, and the preview column gets the
     * whole panel height.
     *
     * The pinning that made us move these actions out of the scrolling column in the first place
     * survives intact, via two mechanisms that cover opposite cases:
     *  - the content wrapper above has `grow`, so with a SHORT proposal list the free space goes
     *    to the wrapper and the bar is pushed to the bottom of the column;
     *  - `position: sticky; bottom: 0` pins the bar to the bottom of the scrollport with a LONG
     *    list, so Apply and its "(N)" counter — the only feedback that a checkbox toggle
     *    registered — stay on screen while the user scrolls and ticks boxes.
     * The background is not decoration: cards scroll UNDER this bar and would otherwise show
     * through it.
     *
     * What this costs, stated plainly: the bar is 400px and column-scoped, so it no longer says
     * "this commits the document on the left" the way a panel-wide footer did. That was the
     * deliberate trade for the preview's height.
     */
    const renderActionBar = () => (
        <Stack
            horizontal horizontalAlign="end" verticalAlign="center"
            tokens={{ childrenGap: 15 }}
            styles={{
                root: {
                    position: "sticky",
                    bottom: 0,
                    flexShrink: 0,
                    padding: "12px 10px",
                    // No top rule, by request. The background is what separates the bar from the
                    // cards passing underneath it, and is therefore not optional.
                    background: "var(--db-bg, #FFFFFF)",
                },
            }}
        >
            {/* Each spinner is a CHILD of the button doing the work, not a sibling of the pair:
                a spinner floating beside two buttons says "something is busy" without saying
                which, and the app's other panels already put it inside (CMFilesUpdatePanel). */}
            {/* Disabled during EITHER write, not just routed around: closeWithOutcome's own
                synchronous-ref guard already makes a click a no-op mid-write, but a
                clickable-looking button that silently does nothing reads as broken. */}
            <DefaultButton text={languageStrings.Close} onClick={closeWithOutcome} disabled={isApplying || isUndoing} />
            <PrimaryButton
                text={`${languageStrings.SuggestPlaceholdersApply} (${checked.size})`}
                onClick={apply}
                disabled={isSuggesting || isPreviewing || isApplying || isUndoing || checked.size === 0}
            >
                {isApplying && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }} />}
            </PrimaryButton>
        </Stack>
    );

    return (
        <WebPanel
            isOpen={props.isOpen}
            width="1100px"
            closePanel={closeWithOutcome}
            headerText={`${languageStrings.SuggestPlaceholdersTitle} — ${props.templateFileName}`}
            styles={previewWebPanelStyles}
        >
            {/* A VIEWPORT height, deliberately, not `flexGrow` inside a flexed `.ms-Panel-content`.
                The flex version was tried and reverted: the row was not in fact capped by it, so
                it grew to its own content height, the panel itself gained a scrollbar, and the
                action bar - correctly pinned to the bottom of a column that was now taller than
                the visible area - appeared stranded in mid-panel. This constant is the geometry
                this panel shipped with, and nothing is subtracted for an action bar because the
                bar lives INSIDE the card column rather than under both. */}
            <Stack horizontal styles={{ root: { height: "calc(100vh - 80px)" } }}>
                {/* Preview first: the document is what is being reviewed and the cards
                    annotate it, so the reading order runs document → decisions.
                    minWidth 0 lets a wide exported table scroll inside this column instead
                    of pushing the card column off the panel.
                    paddingRight is now the ONLY thing separating the two columns - the vertical
                    divider that used to run between them is gone - so it is carrying more than
                    cosmetics: without it the document's white page runs straight into the cards. */}
                <Stack grow styles={{ root: { overflow: "hidden", minWidth: 0, minHeight: 0, paddingRight: 16 } }}>
                    <Text variant="tiny" styles={{ root: { padding: "4px 16px", opacity: 0.6 } }}>
                        {languageStrings.SuggestPlaceholdersApproxLayout}
                    </Text>
                    {renderPreview()}
                </Stack>
                <Stack styles={{ root: { width: 400, flexShrink: 0, minHeight: 0, borderLeft: "1px solid var(--db-border, #EDEBE9)", overflow: "auto" } }}>
                    {/* The scrolling half of the column, wrapped so the action bar below can be
                        its sibling rather than the last thing in the scroll flow.
                        `grow`: with a short proposal list the spare height lands here, which is
                        what pushes the bar down to the bottom of the column.
                        `flexShrink: 0`: with a LONG list there is no spare height, and a
                        shrinkable wrapper would be squeezed below its own content — the cards
                        would spill out of a box the scroller no longer measures, so the bottom of
                        the list could not be reached at all. Refusing to shrink is what makes the
                        column overflow honestly and scroll. */}
                    <Stack grow tokens={{ childrenGap: 8 }} styles={{ root: { flexShrink: 0 } }}>
                        <>
                                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: 10 } }}>
                                    {/* ABOVE the instruction field, not below the button, because it
                                        is not a result: it names which CRM family supplies client
                                        fields for this tenant, or which prerequisite is missing. That
                                        is a precondition for writing the instruction - the placeholder
                                        text invites "use client fields", and this is what says whether
                                        they exist and where they come from - and two of its four forms
                                        end in an instruction to an administrator. Everything below the
                                        Suggest row is per-run output; this is the one line that is not. */}
                                    {vocabularyNote && <MessageBar messageBarType={MessageBarType.info}>{vocabularyNote}</MessageBar>}
                                    {/* An undo lands back HERE, in the review form, so this is
                                        where it has to report itself - the result screen it used
                                        to report from is gone by the time the user sees this.
                                        Dismissible: it describes a finished action, and the user
                                        is now mid-review of the next attempt. */}
                                    {revertNote && (
                                        <MessageBar
                                            messageBarType={MessageBarType.success}
                                            onDismiss={() => setRevertNote(undefined)}
                                        >
                                            {revertNote}
                                        </MessageBar>
                                    )}
                                    {/* The apply's own story, in the review it now lands in:
                                        the count (with its undo), then the skips, then
                                        whichever write last scanned the file. All dismissible.
                                        The close-time host bar re-raises the scan errors, so
                                        dismissing parks detail without losing the record;
                                        dismissing the count bar is dismissing the undo
                                        affordance, and version history remains the fallback -
                                        except mid-undo, when hiding the report would leave a
                                        failed restore nothing to land on. */}
                                    {applyReport && (
                                        <MessageBar
                                            messageBarType={MessageBarType.success}
                                            onDismiss={isUndoing ? undefined : () => setApplyReport(undefined)}                                            actions={applyReport.previousVersion ? (
                                                <MessageBarButton onClick={undo} disabled={isUndoing}>
                                                    {languageStrings.SuggestPlaceholdersUndo}
                                                    {isUndoing && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }} />}
                                                </MessageBarButton>
                                            ) : undefined}
                                        >
                                            {languageStrings.SuggestPlaceholdersAppliedCount
                                                .replace("{0}", String(applyReport.appliedCount))
                                                .replace("{1}", String(applyReport.submittedCount))}
                                        </MessageBar>
                                    )}
                                    {applyReport && applyReport.skipped.length > 0 && (
                                        <MessageBar
                                            messageBarType={MessageBarType.warning}
                                            onDismiss={() => setApplyReport(previous => previous && { ...previous, skipped: [] })}                                        >
                                            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>{languageStrings.SuggestPlaceholdersSkippedOne}</Text>
                                            {applyReport.skipped.map((entry, index) => (
                                                <Text key={index} variant="small" block>
                                                    {entry.label ?? entry.anchorText} — {entry.reason}
                                                </Text>
                                            ))}
                                        </MessageBar>
                                    )}
                                    {writeScanErrors.length > 0 && (
                                        <MessageBar
                                            messageBarType={MessageBarType.warning}
                                            onDismiss={() => setWriteScanErrors([])}                                        >
                                            {writeScanErrors.map((error, index) => (
                                                <Text key={index} variant="small" block>{error}</Text>
                                            ))}
                                        </MessageBar>
                                    )}
                                    <TextField
                                        placeholder={languageStrings.SuggestPlaceholdersInstruction}
                                        value={instruction}
                                        multiline rows={2}
                                        onChange={(_, value) => setInstruction(value ?? "")}
                                    />
                                    {/* Suggest stays here, next to the instruction field it reads from:
                                        it is a GENERATION trigger bound to that input, not a commit,
                                        and an action bar carries commit/dismiss only. Apply is the
                                        commit and lives in the bar at the bottom of this column. */}
                                    {/* paddingTop, not marginTop, to widen the gap under the
                                        instruction field. Fluent's childrenGap emits
                                        `.parent > *:not(:first-child) { margin-top: 8px }`, which
                                        outspecifies a margin set on this child's own class - the
                                        obvious edit here silently does nothing. Padding is not in
                                        that fight, so the gap reads as 8 + 8. */}
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { paddingTop: 8 } }}>
                                        {/* DISABLED while the preview loads, but NOT spinning: there is
                                            nothing to suggest against until the document has landed
                                            (the run needs its fileVersion), yet a spinner here would
                                            claim a suggestion is being generated when none was asked
                                            for. The wait is rendered in the preview column instead.
                                            Only a real suggest run puts the spinner in this button. */}
                                        <DefaultButton
                                            text={languageStrings.SuggestPlaceholdersRun}
                                            onClick={runSuggest}
                                            disabled={isSuggesting || isPreviewing || isApplying || isUndoing}
                                        >
                                            {isSuggesting && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }} />}
                                        </DefaultButton>
                                    </Stack>
                                    {/* Below Suggest, not above it: the rule closes the input block
                                        (instruction + the button that consumes it) rather than
                                        splitting the field from its own trigger. Everything after it
                                        is per-run output. */}
                                    <Separator />
                                    {message && <MessageBar messageBarType={MessageBarType.warning}>{message}</MessageBar>}
                                    {result?.truncated && <MessageBar messageBarType={MessageBarType.info}>{languageStrings.SuggestPlaceholdersTruncated}</MessageBar>}
                                    {/* hasSuggested, not just `result`: on open `result` holds a
                                        preview with no proposals, and announcing "no suggestions
                                        found" before anything was asked for describes a run that
                                        never happened. */}
                                    {!isSuggesting && hasSuggested && proposals.length === 0 && !message && (
                                        <MessageBar messageBarType={MessageBarType.info}>{languageStrings.SuggestPlaceholdersEmpty}</MessageBar>
                                    )}
                                </Stack>
                                {proposals.map(renderCard)}
                                {rejected.length > 0 && (
                                    <Stack styles={{ root: { padding: 10, opacity: 0.6 } }} tokens={{ childrenGap: 6 }}>
                                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>{languageStrings.SuggestPlaceholdersRejected}</Text>
                                        {rejected.map((entry, index) => (
                                            <Text key={index} variant="tiny">
                                                {entry.label ?? entry.anchorText}: {entry.reason}
                                                {entry.nearestLabel ? ` (→ ${entry.nearestLabel}?)` : ""}
                                            </Text>
                                        ))}
                                    </Stack>
                                )}
                        </>
                    </Stack>
                    {renderActionBar()}
                </Stack>
            </Stack>
        </WebPanel>
    );
};

// ---------------------------------------------------------------- PowerPoint filmstrip

interface ISlidePreviewProps {
    slideCount: number;
    fileVersion: string;
    proposals: TemplateAiProposal[];
    checked: Set<number>;
    replaceAll: Set<number>;
    baseRequest: () => Promise<any>;
    onToggle: (id: number) => void;
    /** Card the user just clicked; the filmstrip jumps to the slide that carries it. */
    focusedProposal?: number;
}

/** One slide at a time, re-rendered server-side (debounced) when the accepted subset for
 *  that slide changes; bounds become click targets over the image. */
const SlidePreview = ({ slideCount, fileVersion, proposals, checked, replaceAll, baseRequest, onToggle, focusedProposal }: ISlidePreviewProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [slideIndex, setSlideIndex] = React.useState(0);

    // Card -> slide sync (design §7.3). Only ever moves TO a proposal's slide: a proposal
    // with no slideIndex (or one already on screen) must not yank the user somewhere else.
    React.useEffect(() => {
        if (focusedProposal === undefined) return;
        const target = proposals.find(p => p.id === focusedProposal)?.slideIndex;
        if (typeof target === "number" && target >= 0 && target < slideCount) setSlideIndex(target);
    }, [focusedProposal, proposals, slideCount]);
    const [render, setRender] = React.useState<RenderProposalSlideResponse | undefined>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [renderError, setRenderError] = React.useState<string | undefined>();
    const requestIdRef = React.useRef(0);

    // Memo of states already rendered. Every entry belongs to ONE fileVersion, so a version
    // change drops the lot rather than risking an image of a template that has since moved.
    //
    // Lazily initialised: useRef evaluates its argument on EVERY render and keeps only the
    // first result, so `useRef(createSlideRenderCache())` allocated a throwaway Map and
    // closure per render for the lifetime of the panel.
    const cacheRef = React.useRef<ISlideRenderCache>(null);
    cacheRef.current ??= createSlideRenderCache();
    React.useEffect(() => { cacheRef.current.clear(); }, [fileVersion]);

    React.useEffect(() => {
        if (slideCount === 0) return undefined; // the component renders null; don't fetch either
        const requestId = ++requestIdRef.current; // stale-response guard

        // Replace-all proposals affect every slide their anchor appears on, so they ride
        // along regardless of their first-occurrence slideIndex.
        const accepted = proposals
            .filter(p => checked.has(p.id) && (p.slideIndex === slideIndex || replaceAll.has(p.id)))
            .map(p => ({ ...p, replaceAll: replaceAll.has(p.id) }));

        // Cache hit: no debounce, no spinner, no round trip. Toggling a proposal on, off and
        // on again is the dominant review gesture and used to cost a full server render each
        // time (download + parse + rasterise).
        const key = buildSlideRenderKey(slideIndex, accepted);
        const cached = cacheRef.current.get(key);
        if (cached) {
            setRender(cached);
            setRenderError(undefined);
            setIsLoading(false);
            return undefined;
        }

        const timer = setTimeout(async () => {
            setIsLoading(true);
            try {
                const response = await apiRenderProposalSlide({
                    ...(await baseRequest()),
                    fileVersion,
                    slideIndex,
                    acceptedProposals: accepted,
                });
                // Only successful renders are memoised - caching a failure would make one
                // transient 500 permanent for that state until the version changes.
                if (response) cacheRef.current.set(key, response);
                if (requestId === requestIdRef.current) {
                    setRender(response);
                    setRenderError(undefined);
                }
            } catch (error: any) {
                // The 409 (template changed since suggest) MUST reach the user - a stale
                // image with no message reads as "everything is fine".
                if (requestId === requestIdRef.current) {
                    setRenderError(errorText(error, languageStrings.ErrorMessageGeneric));
                }
            } finally {
                if (requestId === requestIdRef.current) setIsLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [slideIndex, slideCount, checked, replaceAll, proposals, fileVersion, baseRequest, languageStrings]);

    if (slideCount === 0) return null;

    return (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: 16, overflow: "auto" } }}>
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                {/* Fluent icons rather than ◀/▶ text: the raw geometric glyphs render as
                    garbled characters on some Office/Windows font stacks, and an icon button
                    carries the accessible name a bare glyph never had. */}
                <IconButton
                    iconProps={{ iconName: "ChevronLeft" }}
                    ariaLabel={languageStrings.SuggestPlaceholdersPreviousSlide}
                    title={languageStrings.SuggestPlaceholdersPreviousSlide}
                    disabled={slideIndex <= 0}
                    onClick={() => setSlideIndex(index => index - 1)}
                />
                <Text>{slideIndex + 1} / {slideCount}</Text>
                <IconButton
                    iconProps={{ iconName: "ChevronRight" }}
                    ariaLabel={languageStrings.SuggestPlaceholdersNextSlide}
                    title={languageStrings.SuggestPlaceholdersNextSlide}
                    disabled={slideIndex >= slideCount - 1}
                    onClick={() => setSlideIndex(index => index + 1)}
                />
                {isLoading && <Spinner size={SpinnerSize.small} />}
            </Stack>
            {renderError && <MessageBar messageBarType={MessageBarType.warning}>{renderError}</MessageBar>}
            {/* Never frozen: after an apply the panel swaps in a fresh preview whose version
                label is the one the server just minted, and after an undo the restored
                snapshot carries the refreshed label - the effect above always renders against
                current truth, so there is no stale picture to suppress and nothing to freeze. */}
            {render && (
                <div style={{ position: "relative", width: "100%" }}>
                    <img
                        src={`data:image/png;base64,${render.imageBase64}`}
                        style={{ width: "100%", display: "block" }}
                    />
                    {render.slideWidth > 0 && render.slideHeight > 0 && (render.bounds ?? []).map((bounds, index) => (
                        <div
                            key={index}
                            onClick={() => onToggle(bounds.proposalId)}
                            title={proposals.find(p => p.id === bounds.proposalId)?.displayLabel}
                            style={{
                                position: "absolute",
                                cursor: "pointer",
                                // A fully transparent target is undiscoverable: the region is
                                // outlined, and the card the user just clicked is emphasised so
                                // the card -> slide jump above is visible rather than silent.
                                outline: bounds.proposalId === focusedProposal
                                    ? "2px solid var(--db-accent, #0078D4)"
                                    : "1px dashed var(--db-border, #8A8886)",
                                left: `${(bounds.x / render.slideWidth) * 100}%`,
                                top: `${(bounds.y / render.slideHeight) * 100}%`,
                                width: `${(bounds.width / render.slideWidth) * 100}%`,
                                height: `${(bounds.height / render.slideHeight) * 100}%`,
                            }}
                        />
                    ))}
                </div>
            )}
        </Stack>
    );
};
